/**
 * TitanBikes Navigation Controller (v5)
 * Only active on large screens (>= 992px) to prevent mobile/tablet layout conflicts.
 * Ensures:
 * 1. Primary dropdown card is flush with header bottom line.
 * 2. Secondary flyout card is positioned with 10px top space and 12px gap.
 * 3. Robust hover buffering and diagonal travel tolerance for child submenus.
 * 4. Automatic screen boundary detection (reverse-flyout).
 * 5. Touch/click support for tablet landscape viewports.
 */
document.addEventListener('DOMContentLoaded', () => {
  const isLargeScreen = () => window.innerWidth >= 992;
  const navItems = document.querySelectorAll('.titan-nav-item');
  const HOVER_BUFFER_MS = 280;

  navItems.forEach((item) => {
    const dropdown = item.querySelector('.titan-dropdown');
    if (!dropdown) return;

    let timeoutId = null;

    // Flush positioning helper for primary dropdown
    const alignWithHeaderBottom = () => {
      if (!isLargeScreen()) return;
      const header = item.closest('.site-header') || item.closest('header');
      if (header) {
        const headerBottom = header.getBoundingClientRect().bottom;
        const itemTop = item.getBoundingClientRect().top;
        const offset = Math.round(headerBottom - itemTop);
        dropdown.style.top = `${offset}px`;

        // Ensure primary dropdown doesn't overflow right viewport edge
        const card = dropdown.querySelector('.titan-dropdown-card');
        if (card) {
          const itemRect = item.getBoundingClientRect();
          const cardWidth = card.offsetWidth || 240;
          if (itemRect.left + cardWidth > window.innerWidth - 15) {
            const shift = (itemRect.left + cardWidth) - (window.innerWidth - 15);
            dropdown.style.left = `-${shift}px`;
          } else {
            dropdown.style.left = '0px';
          }
        }
      }
    };

    // Helper to position a nested dropdown relative to its card
    const positionNestedDropdown = (nested) => {
      const card = dropdown.querySelector('.titan-dropdown-card');
      if (!card || !nested) return;

      const cardWidth = card.offsetWidth;
      const cardRect = card.getBoundingClientRect();
      const gap = 12;

      nested.style.top = '10px';
      const nestedWidth = nested.offsetWidth || 260;

      if (cardRect.right + nestedWidth + gap > window.innerWidth - 15) {
        dropdown.classList.add('reverse-flyout');
        nested.style.left = 'auto';
        nested.style.right = `${cardWidth + gap}px`;
      } else {
        dropdown.classList.remove('reverse-flyout');
        nested.style.right = 'auto';
        nested.style.left = `${cardWidth + gap}px`;
      }
    };

    // Position all nested dropdowns
    const positionAllNested = () => {
      const nestedDropdowns = dropdown.querySelectorAll('.titan-nested-dropdown');
      nestedDropdowns.forEach(positionNestedDropdown);
    };

    // Hover intent entry for top-level item
    item.addEventListener('mouseenter', () => {
      if (!isLargeScreen()) return;
      clearTimeout(timeoutId);
      item.classList.add('is-open');
      alignWithHeaderBottom();
      positionAllNested();
    });

    // Hover intent exit with buffer delay
    item.addEventListener('mouseleave', () => {
      if (!isLargeScreen()) return;
      timeoutId = setTimeout(() => {
        item.classList.remove('is-open');
        // Also close any active nested submenus when top-level closes
        dropdown.querySelectorAll('.is-nested-open').forEach((el) => {
          el.classList.remove('is-nested-open');
        });
      }, HOVER_BUFFER_MS);
    });

    // -------------------------------------------------------------
    // Nested Submenu (Flyout) Controller with Diagonal Travel Buffer
    // -------------------------------------------------------------
    const card = dropdown.querySelector('.titan-dropdown-card');
    if (card) {
      const allItems = Array.from(card.querySelectorAll('.titan-dropdown-item'));
      const nestedItems = allItems.filter((i) => i.querySelector('.titan-nested-dropdown'));
      const nonNestedItems = allItems.filter((i) => !i.querySelector('.titan-nested-dropdown'));
      let activeNestedItem = null;
      let nestedCloseTimer = null;
      let switchTimer = null;

      const openNested = (targetItem) => {
        clearTimeout(nestedCloseTimer);
        clearTimeout(switchTimer);
        clearTimeout(timeoutId); // Keep top-level open

        if (activeNestedItem && activeNestedItem !== targetItem) {
          activeNestedItem.classList.remove('is-nested-open');
        }

        activeNestedItem = targetItem;
        targetItem.classList.add('is-nested-open');

        const nestedMenu = targetItem.querySelector('.titan-nested-dropdown');
        if (nestedMenu) {
          positionNestedDropdown(nestedMenu);
        }
      };

      const closeNestedWithDelay = (delay = HOVER_BUFFER_MS) => {
        clearTimeout(nestedCloseTimer);
        nestedCloseTimer = setTimeout(() => {
          if (activeNestedItem) {
            activeNestedItem.classList.remove('is-nested-open');
            activeNestedItem = null;
          }
        }, delay);
      };

      nestedItems.forEach((subItem) => {
        const nestedMenu = subItem.querySelector('.titan-nested-dropdown');
        const triggerLink = subItem.querySelector('.titan-sub-link');

        subItem.addEventListener('mouseenter', () => {
          if (!isLargeScreen()) return;
          clearTimeout(nestedCloseTimer);
          clearTimeout(timeoutId);

          if (activeNestedItem && activeNestedItem !== subItem) {
            // Sibling switch buffer: if mouse is rapidly sweeping across, delay switch
            clearTimeout(switchTimer);
            switchTimer = setTimeout(() => {
              openNested(subItem);
            }, 120);
          } else {
            openNested(subItem);
          }
        });

        subItem.addEventListener('mouseleave', (e) => {
          if (!isLargeScreen()) return;
          clearTimeout(switchTimer);

          // If mouse is moving directly into the nested menu, do not schedule close
          if (nestedMenu && (nestedMenu === e.relatedTarget || nestedMenu.contains(e.relatedTarget))) {
            return;
          }
          closeNestedWithDelay(280);
        });

        // Hover listeners on the nested menu card itself
        if (nestedMenu) {
          nestedMenu.addEventListener('mouseenter', () => {
            if (!isLargeScreen()) return;
            clearTimeout(nestedCloseTimer);
            clearTimeout(switchTimer);
            clearTimeout(timeoutId);

            if (activeNestedItem) {
              activeNestedItem.classList.add('is-nested-open');
            }
            item.classList.add('is-open');
          });

          nestedMenu.addEventListener('mouseleave', (e) => {
            if (!isLargeScreen()) return;
            // If mouse moves back into the parent trigger item, keep open
            if (subItem === e.relatedTarget || subItem.contains(e.relatedTarget)) {
              return;
            }
            closeNestedWithDelay(280);
          });
        }

        // Touch & Click toggle support
        if (triggerLink) {
          triggerLink.addEventListener('click', (e) => {
            if (!isLargeScreen()) return;
            if (nestedMenu) {
              if (!subItem.classList.contains('is-nested-open')) {
                e.preventDefault();
                e.stopPropagation();
                openNested(subItem);
              }
            }
          });
        }
      });

      // Sibling items that do NOT have a nested menu:
      // Give a gentle buffer so crossing over them diagonally doesn't immediately close the open submenu
      nonNestedItems.forEach((nonItem) => {
        nonItem.addEventListener('mouseenter', () => {
          if (!isLargeScreen()) return;
          clearTimeout(switchTimer);
          if (activeNestedItem) {
            closeNestedWithDelay(180);
          }
        });
      });
    }

    // Large touch device support (e.g. iPad Pro in landscape)
    const trigger = item.querySelector('.titan-nav-link');
    if (trigger && 'ontouchstart' in window) {
      trigger.addEventListener('click', (e) => {
        if (!isLargeScreen()) return;
        if (!item.classList.contains('is-open')) {
          e.preventDefault();
          alignWithHeaderBottom();
          positionAllNested();
          navItems.forEach((other) => {
            if (other !== item) other.classList.remove('is-open');
          });
          item.classList.add('is-open');
        }
      });
    }
  });

  // Re-align on window resize or reset on mobile switch
  window.addEventListener('resize', () => {
    if (!isLargeScreen()) {
      navItems.forEach((item) => {
        item.classList.remove('is-open');
        item.querySelectorAll('.is-nested-open').forEach((n) => n.classList.remove('is-nested-open'));
      });
      return;
    }

    const openItem = document.querySelector('.titan-nav-item.is-open');
    if (openItem) {
      const dropdown = openItem.querySelector('.titan-dropdown');
      const header = openItem.closest('.site-header') || openItem.closest('header');
      if (dropdown && header) {
        const offset = Math.round(header.getBoundingClientRect().bottom - openItem.getBoundingClientRect().top);
        dropdown.style.top = `${offset}px`;
      }
    }
  });

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.titan-nav-wrapper')) {
      navItems.forEach((item) => {
        item.classList.remove('is-open');
        item.querySelectorAll('.is-nested-open').forEach((n) => n.classList.remove('is-nested-open'));
      });
    }
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      navItems.forEach((item) => {
        item.classList.remove('is-open');
        item.querySelectorAll('.is-nested-open').forEach((n) => n.classList.remove('is-nested-open'));
      });
    }
  });
});
